import HelperFunctions from '../../../../../global/HelperFunctions';
import { useGetLabelFolders } from 'pages/labels/hooks/useGetLabelFolders';
import Constants from 'config/Constants';
import { useContext, useMemo } from 'react';
import { Badge, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { set } from 'features/app/appSlice';
import { isEmpty } from 'helpers/StringHelper';

const defaultContent = {
    labelFolder: '',
    options: [],
};

export function LabelBlock({ block, baseBlock, index, readOnly = false }) {
    const { hideChanges } = useContext(EditAreaContext);
    const { values } = useFormikContext();
    const changes = baseBlock?.changes ?? [];

    const allChangesProcessed = changes.every((blockChange) => {
        const changeInForm = values.changes.find((formChange) => formChange.id === blockChange.id);

        return changeInForm?.processed === true;
    });

    const allAccepted = changes.every((blockChange) => {
        const changeInForm = values.changes.find((formChange) => formChange.id === blockChange.id);

        return changeInForm?.processed === true && changeInForm?.accepted === true;
    });

    const latestContent = useMemo(() => {
        const content = (() => {
            if (baseBlock && baseBlock.changes && baseBlock.changes.length > 0) {
                if (allChangesProcessed) {
                    return allAccepted ? block.latestContent : block.baseContent;
                }
            }

            if (baseBlock && !hideChanges && !isEmpty(baseBlock.diffContent)) {
                return baseBlock.diffContent;
            }

            if (!hideChanges && !isEmpty(block.diffContent)) {
                return block.diffContent;
            }

            return block.latestContent;
        })();

        return HelperFunctions.tryParseJSON(content, defaultContent);
    }, [block, baseBlock, hideChanges, allChangesProcessed, allAccepted]);

    if (readOnly) {
        return <div className="p-4">{renderLabelBlockContent()}</div>;
    }

    return (
        <ClickableBlock block={block} index={index}>
            {renderLabelBlockContent()}
        </ClickableBlock>
    );

    function renderLabelBlockContent() {
        return (
            <LabelBlockContent
                block={block}
                latestContent={latestContent}
                index={index}
                readOnly={readOnly}
                hideChanges={hideChanges}
            />
        );
    }
}

function ClickableBlock({ block, index, children }) {
    const dispatch = useDispatch();

    return (
        <div className="p-4 cursor-pointer" onClick={handleClick}>
            {children}
        </div>
    );

    function handleClick(e) {
        e.preventDefault();

        dispatch(
            set({
                key: 'blockEditModal',
                value: {
                    eventKey: 'labelBlockModal',
                    block,
                    blockIndex: index,
                },
            }),
        );
    }
}

function LabelBlockContent({ block, latestContent, index, readOnly, hideChanges }) {
    const { labelFolder } = latestContent;
    const { t } = useTranslation('documents');
    const folders = useGetLabelFolders();

    // Filter folders
    const allowedTypes = Object.keys(Constants.labelFolderEntities).filter((_entity) => {
        return Constants.labelFolderEntities[_entity].includes('labelBlock');
    });

    const availableFolders = folders.filter((_folder) =>
        _folder.contentTypes.some((_type) => allowedTypes.includes(_type)),
    );

    const folderOption = availableFolders.find((folder) => folder.id === labelFolder);

    return (
        <div className="pt-3">
            <Form.Group as={Row} className="mb-0">
                <Form.Label column sm={4} htmlFor="labelFolder">
                    {t('document.navbar.main.editor.left.blocks.types.labelBlock.map')}
                </Form.Label>
                <Col sm={8}>
                    <input
                        type="text"
                        readOnly
                        className="form-control-plaintext"
                        id="labelFolder"
                        value={folderOption?.name ?? '-'}
                    />
                </Col>
            </Form.Group>

            {labelFolder !== '' && (
                <Form.Group as={Row}>
                    <Form.Label column sm={4} htmlFor="options">
                        {t('document.navbar.main.editor.left.blocks.types.labelBlock.value')}
                    </Form.Label>
                    <Col sm={8}>
                        <div style={{ paddingTop: '0.6rem' }}>
                            <LabelBlockOptions block={block} latestContent={latestContent} hideChanges={hideChanges} />
                        </div>
                    </Col>
                </Form.Group>
            )}
        </div>
    );
}

function LabelBlockOptions({ block, latestContent, hideChanges = false }) {
    const { t } = useTranslation('documents');

    const multiple = block.properties.multiple === true;
    const { options = [], changes = {} } = latestContent;

    if (options.length === 0) {
        return <>-</>;
    }

    if (!multiple && options.length === 2 && Object.keys(changes).length === 2) {
        return <SingleItemChanges latestContent={latestContent} />;
    }

    return (
        <>
            {options.map((_option, index) => {
                let variant = 'primary';

                if (!hideChanges && changes.hasOwnProperty(_option.id)) {
                    const changeType = changes[_option.id];
                    variant = changeType === 'a' ? 'success ' : 'danger';
                }

                return (
                    <Badge
                        className="mr-1 label-block-text-color"
                        variant={variant}
                        key={`block-${block.key}-option-${index}`}
                    >
                        {_option.label}
                    </Badge>
                );
            })}
        </>
    );
}

function SingleItemChanges({ latestContent }) {
    const { options = [], changes = {} } = latestContent;

    const optionOne = options[0];
    const optionTwo = options[1];

    let oldLabel = optionOne.label;
    let newLabel = optionTwo.label;

    // optionOne is added, so reverse the labels
    if (changes[optionOne.id] === 'a') {
        oldLabel = optionTwo.label;
        newLabel = optionOne.label;
    }

    return (
        <Badge variant="warning">
            <del>{oldLabel}</del>
            <ins>{newLabel}</ins>
        </Badge>
    );
}
